/* ============================================================================
   SIZE CHART — Figma 877:185610: the Women's Tops component (877:181200)
   on the Catarina PDP, Women's Pants (877:182048) on the Isabel PDP
   (Tab=Default / Size Selected / Styles × Mobile / Desktop).

   SIZING: unit toggle (IN / CM), a size menu that fills the summary box and
   marks the row, the body-measurement table (its US/CA header is the region
   menu), How to Measure and Need Help. STYLES + FIT: the core styles side by
   side on full-width zebra rows. Opened from the size drawer's SIZE CHART;
   the size picked here is only a lookup — the PDP's own size stays the PDP's.
   The pants chart adds the inseam table (each core pant × length). The
   chart is data; the drawer markup is the shell both pages share.
   ========================================================================== */

import { stagger, MO, state, play, $, $$ } from './mo.js';
import { createDrawer } from './drawers.js';
import { swapText, tm } from './ui.js';

/* The two charts. Tops: the Women's Tops component (877:181200), bust and
   waist. Pants: the Women's Pants component (877:182048) — waist and hips,
   the inseam of each core pant by length, How to Measure for all three, and
   its Styles + Fit. Its table stops at 3XL; 4XL–6XL continue its own steps
   (+3” waist and hips, W sizes) so the 6XL the size drawer offers reads too.
   Measurements are inches; US/CA sizes as the tables print them. The men's
   charts (Men's Tops, Men's Pants) are the live kit page's (wearfigs.com/
   kits/mens-the-set, 2026-10-21): no US/CA column (`regions: false`), and
   Men's Pants has no Styles + Fit tab, as live. */
const CHARTS = {
  tops: {
    subtitle: 'Scrub Tops',
    cols: ['Bust', 'Waist'],
    rows: [
      ['XXS', [29.5, 31.5], [23, 25], ['00', '0']],
      ['XS', [31.5, 33.5], [25, 27], ['0', '2']],
      ['S', [33.5, 35.5], [27, 29], ['4', '6']],
      ['M', [35.5, 37.5], [29, 31], ['8', '10']],
      ['L', [37.5, 40], [31, 33.5], ['12']],
      ['XL', [40, 42], [33.5, 36], ['14']],
      ['2XL', [42.5, 45.5], [36, 39], ['16']],
      ['3XL', [45.5, 49], [39, 44], ['18', '20']],
      ['4XL', [49, 52], [44, 47], ['22', '24']],
      ['5XL', [52, 55], [47, 50], ['26', '28']],
      ['6XL', [55, 58], [50, 53], ['30', '32']],
    ],
    how: [
      ['Bust', 'Wrap the tape around the fullest part of your chest and keep the tape level across your back.', 'assets/figma/measure-bust.webp'],
      ['Waist', 'Measure around the narrowest part of your natural waistline.', 'assets/figma/measure-waist.webp'],
    ],
    lengths: null,
    styles: {
      title: 'Core Styles',
      lede: 'Our best-sellers—on call 24/7, 365.',
      list: [
        { name: 'Catarina™', image: 'assets/figma/style-1.webp', cells: ['1 pocket', 'Classic Fit, Petite Fit', 'V-Neck'] },
        { name: 'Rafaela™', image: 'assets/figma/style-2.webp', cells: ['3 pockets', 'Oversized Fit', 'Mandarin Collar'] },
        { name: 'Casma™', image: 'assets/figma/style-3.webp', cells: ['3 pockets', 'Classic Fit', 'V-Neck'] },
        { name: 'Montex', image: 'assets/figma/style-4.webp', cells: ['3 pockets', 'Relaxed Fit', 'Mock Neck'] },
      ],
    },
  },
  pants: {
    subtitle: 'Scrub Pants',
    cols: ['Waist', 'Hips'],
    rows: [
      ['XXS', [23, 24], [33, 34], ['00', '0']],
      ['XS', [25, 26.5], [35, 36.5], ['0', '2']],
      ['S', [27, 28.5], [37, 38.5], ['4', '6']],
      ['M', [29, 31], [39, 41], ['8', '10']],
      ['L', [32, 34], [42, 43], ['12']],
      ['XL', [35, 37], [44, 46], ['14']],
      ['2XL', [38, 40], [47, 49], ['16']],
      ['3XL', [41.5, 43], [50.5, 52], ['16W', '18W']],
      ['4XL', [44.5, 46], [53.5, 55], ['20W', '22W']],
      ['5XL', [47.5, 49], [56.5, 58], ['24W', '26W']],
      ['6XL', [50.5, 52], [59.5, 61], ['28W', '30W']],
    ],
    how: [
      ['Waist', 'Measure around the narrowest part of your natural waistline.', 'assets/figma/measure-pant-waist.webp'],
      ['Hips', 'Keep the tape loose enough to stay relaxed, but snug enough to stay in place—measure around the widest part of your hips and seat.', 'assets/figma/measure-pant-hips.webp'],
      ['Inseam', 'Take your favorite pair of pants and measure the inside leg from the bottom of the crotch seam to ankle hem.', 'assets/figma/measure-pant-inseam.webp'],
    ],
    lengths: {
      cols: ['Petite (5’3” and below)', 'Regular (5’4” - 5’7”)', 'Tall (5’8” and above)'],
      rows: [['Zamora™', 26.5, 28.5, 30.5], ['Yola™', 28, 30, 32], ['Livingston™', 28.5, 30.5, 32], ['Kade™', 29, 31, 33], ['Isabel', 29, 31, 32.5]],
    },
    styles: {
      title: 'Core Styles',
      lede: 'Best-selling scrub pants that go with everything—for days (and nights) when you’re doing it all.',
      list: [
        { name: 'Isabel', image: 'assets/figma/pant-style-isabel.webp', cells: ['8 pockets', 'Relaxed Fit', 'Wide Leg'] },
        { name: 'Zamora™', image: 'assets/figma/pant-style-zamora.webp', cells: ['6 Pockets', 'Classic Fit', 'Jogger Leg'] },
        { name: 'Kade™', image: 'assets/figma/pant-style-kade.webp', cells: ['4 Pockets', 'Classic Fit', 'Straight Leg'] },
        { name: 'Livingston™', image: 'assets/figma/pant-style-livingston.webp', cells: ['3 Pockets', 'Classic Fit', 'Straight Leg'] },
        { name: 'Yola™', image: 'assets/figma/pant-style-livingston.webp', cells: ['11 Pockets', 'Slim Fit', 'Straight Leg'] },
      ],
    },
  },
  'mens-tops': {
    subtitle: 'Men’s Tops',
    cols: ['Chest', 'Waist'],
    regions: false,
    rows: [
      ['XS', [34, 36], [28.5, 30.5]], ['S', [36, 38], [30.5, 32.5]], ['M', [38, 40], [32.5, 34.5]],
      ['L', [40, 42.5], [34.5, 37]], ['XL', [42.5, 45], [37, 39.5]], ['2XL', [45, 48.5], [39.5, 43]],
      ['3XL', [48.5, 52], [43, 47]], ['4XL', [52, 55], [47, 50]], ['5XL', [55, 58], [50, 53]], ['6XL', [58, 61], [53, 56]],
    ],
    how: [
      ['Chest', 'Fullest part of your chest, tape parallel to the floor.', 'assets/set/mens-chart/measure-chest.webp'],
      ['Waist', 'The narrowest part of your natural waistline.', 'assets/set/mens-chart/measure-waist.webp'],
    ],
    lengths: null,
    styles: {
      title: 'Core Styles',
      lede: 'Our best-sellers—on call 24/7, 365.',
      list: [
        { name: 'Leon™', image: 'assets/set/mens-chart/style-leon.webp', cells: ['3 Pockets', 'Classic Fit', 'Classic V-Neck'] },
        { name: 'Chisec™', image: 'assets/set/mens-chart/style-chisec.webp', cells: ['1 Chest, 2 Side Pockets', 'Classic Fit', 'Modern V-Neck'] },
        { name: 'Arad', image: 'assets/set/mens-chart/style-arad.webp', cells: ['3 Pockets', 'Classic Fit', 'Polo'] },
        { name: 'Surrey', image: 'assets/set/mens-chart/style-surrey.webp', cells: ['2 Pockets', 'Classic Fit', 'Modern V-Neck'] },
        { name: 'Timmons', image: 'assets/set/mens-chart/style-timmons.webp', cells: ['4 Pockets', 'Classic Fit', 'Mock Neck'] },
      ],
    },
  },
  'mens-pants': {
    subtitle: 'Men’s Pants',
    cols: ['Waist', 'Hip'],
    regions: false,
    rows: [
      ['XS', [28.5, 30.5], [33.5, 35.5]], ['S', [30.5, 32.5], [35.5, 37.5]], ['M', [32.5, 34.5], [37.5, 39.5]],
      ['L', [34.5, 37], [39.5, 42]], ['XL', [37, 39.5], [42, 44.5]], ['2XL', [39.5, 43], [44.5, 48]],
      ['3XL', [43, 47], [48, 52.5]], ['4XL', [47, 50], [52.5, 55.5]], ['5XL', [50, 53], [55.5, 58.5]], ['6XL', [53, 56], [58.5, 61.5]],
    ],
    how: [
      ['Waist', 'The narrowest part of your natural waistline.', 'assets/set/mens-chart/measure-waist.webp'],
      ['Hips', 'The fullest part of your hips and seat.', 'assets/set/mens-chart/measure-hips.webp'],
      ['Length', 'Measure from the crotch along the inner side of the leg.', 'assets/set/mens-chart/measure-inseam.webp'],
    ],
    lengths: {
      cols: ['Short (5’7” and below)', 'Regular (5’8” - 6’)', 'Tall (6’1” and above)'],
      rows: [['Tansen', 27, 29, 31], ['Cairo', 28, 30, 32], ['Axim', 28, 30, 32], ['Pisco', 28, 30, 32]],
    },
    styles: null,
  },
};
/* Regions — standard womenswear offsets from US (UK +4, EU +32; US 00 = −2);
   a W size keeps its W. */
const REGIONS = { 'US/CA': (n) => n, UK: (n) => n + 4, EU: (n) => n + 32 };
const usNum = (s) => (s === '00' ? -2 : parseFloat(s));

/* the page's product decides the chart (a set's, its first product's); the
   cross-sell's tops ask for theirs */
export function initSizeChart({ product } = {}) {
  const pageChart = product?.kit ? product.components[0].chart : product?.id === 'isabel' ? 'pants' : 'tops';
  const root = $('[data-drawer="sizechart"]');
  const drawer = createDrawer(root);
  const tabs = $$('[role="tab"]', root);
  const panels = $$('[role="tabpanel"]', root);
  const units = $$('[data-sc-unit]', root);
  const select = $('[data-sc-size]', root);
  const region = $('[data-sc-region]', root);
  const regionLabel = $('[data-sc-region-label]', root);
  const summary = $('[data-sc-summary]', root);
  const body = $('[data-sc-rows]', root);
  const scroller = $('.sc', root);
  let unit = 'in';
  let reg = 'US/CA';
  let size = '';
  let chart = null;
  let C = CHARTS[pageChart];

  const inch = (v) => (unit === 'in' ? v : Math.round(v * 2.54));
  const measure = ([a, b]) => `${inch(a)} - ${inch(b)}${unit === 'in' ? '”' : ' cm'}`;
  const sizes = (list) => list.map((s) => {
    const w = /w$/i.test(s) ? 'W' : '';
    const n = REGIONS[reg](usNum(s));
    return `${n === -2 ? '00' : n}${w}`;
  }).join(' - ');

  function renderRows() {
    body.innerHTML = C.rows.map(([s, a, b, us]) => `
      <tr class="sc-table__row" data-sc-row="${s}"${s === size ? ' aria-selected="true"' : ''}>
        <th class="sc-table__cell sc-table__cell--size" scope="row">${s}</th>
        <td class="sc-table__cell">${measure(a)}</td>
        <td class="sc-table__cell">${measure(b)}</td>
        ${us ? `<td class="sc-table__cell">${sizes(us)}</td>` : ''}
      </tr>`).join('');
    const lengths = $('[data-sc-lengths]', root);
    if (!lengths) return;
    lengths.hidden = !C.lengths;
    if (!C.lengths) return;
    $('[data-sc-length-head]', root).innerHTML = `<th class="sc-table__cell sc-table__cell--size" scope="col">Product</th>${C.lengths.cols.map((c) => `<th class="sc-table__cell" scope="col">${c}</th>`).join('')}`;
    $('[data-sc-length-rows]', root).innerHTML = C.lengths.rows.map(([name, ...v]) => `
      <tr class="sc-table__row"><th class="sc-table__cell sc-table__cell--size" scope="row">${tm(name)}</th>${v.map((x) => `<td class="sc-table__cell">${inch(x)}${unit === 'in' ? '”' : ' cm'}</td>`).join('')}</tr>`).join('');
  }

  /* the chart's own words and pictures — swapped only when the chart changes */
  function useChart(key) {
    if (key === chart) return;
    chart = key;
    C = CHARTS[key];
    $('[data-sc-subtitle]', root).textContent = C.subtitle;
    $$('[data-sc-col]', root).forEach((th, i) => { th.textContent = C.cols[i]; });
    $$('[data-sc-summary-label]', root).forEach((el, i) => { el.textContent = C.cols[i]; });
    $('[data-sc-table]', root).setAttribute('aria-label', `Body measurements by size: ${C.cols.join(' and ').toLowerCase()}`);
    region.closest('th').hidden = C.regions === false;
    region.closest('table').classList.toggle('sc-table--no-region', C.regions === false);
    $('[data-sc-summary-region]', root).closest('.sc-summary__item').hidden = C.regions === false;
    tabs[1].hidden = !C.styles;
    select.innerHTML = `<option value="">Select a size</option>${C.rows.map(([s]) => `<option>${s}</option>`).join('')}`;
    $('[data-sc-how]', root).innerHTML = C.how.map(([title, text, img]) => `
      <div class="sc-how">
        <img class="sc-how__img" src="${img}" alt="" width="120" height="150" loading="lazy">
        <div class="sc-how__body"><p class="sc-how__title">${title}</p><p class="sc-how__text">${text}</p></div>
      </div>`).join('');
    if (!C.styles) return;
    $('[data-sc-styles-title]', root).textContent = C.styles.title;
    $('[data-sc-styles-lede]', root).textContent = C.styles.lede;
    $('[data-sc-styles]', root).innerHTML = C.styles.list.map((st) => `
      <li class="sc-style">
        <span class="sc-style__media"><img class="sc-style__img" src="${st.image}" alt="" width="134" height="168" loading="lazy" decoding="async"></span>
        <span class="sc-style__cell sc-style__cell--name">${tm(st.name)}</span>
        ${st.cells.map((c, i) => `<span class="sc-style__cell${i % 2 ? '' : ' sc-style__cell--zebra'}">${c}</span>`).join('')}
      </li>`).join('');
  }

  function renderSummary(animate) {
    const row = C.rows.find((r) => r[0] === size);
    summary.hidden = !row;
    if (!row) return;
    const values = [measure(row[1]), measure(row[2]), row[3] ? sizes(row[3]) : ''];
    $$('[data-sc-value]', summary).forEach((el, i) => {
      if (animate) swapText(el, values[i]); else el.textContent = values[i];
    });
    $('[data-sc-summary-region]', summary).textContent = reg;
  }

  function setSize(next, { animate = true } = {}) {
    const first = !size && next;
    size = next;
    select.value = next;
    $$('[data-sc-row]', body).forEach((r) => r.toggleAttribute('aria-selected', r.dataset.scRow === size));
    renderSummary(animate && !first);
    if (first && animate && !state.reduced) {
      play(summary, { opacity: [0, 1], y: [-MO.riseXs, 0] }, { duration: MO.base, ease: MO.eEntrance });
    }
  }

  select.addEventListener('change', () => setSize(select.value));

  units.forEach((u) => u.addEventListener('click', () => {
    if (u.dataset.scUnit === unit) return;
    unit = u.dataset.scUnit;
    units.forEach((x) => x.setAttribute('aria-pressed', String(x.dataset.scUnit === unit)));
    renderRows();
    renderSummary(true);
  }));

  region.addEventListener('change', () => {
    reg = region.value;
    regionLabel.textContent = reg;
    renderRows();
    renderSummary(true);
  });

  /* tabs — WAI pattern: arrows move between tabs, the panel follows */
  function selectTab(tab) {
    tabs.forEach((t) => {
      const on = t === tab;
      t.setAttribute('aria-selected', String(on));
      t.tabIndex = on ? 0 : -1;
    });
    panels.forEach((p) => { p.hidden = p.id !== tab.getAttribute('aria-controls'); });
    scroller.scrollTop = 0;
    const shown = panels.find((p) => !p.hidden);
    if (!state.reduced) {
      play([...shown.children], { opacity: [0, 1], y: [MO.riseXs, 0] },
        { duration: MO.base, ease: MO.eEntrance, delay: stagger(MO.stagTight) });
    }
  }
  tabs.forEach((t, i) => {
    t.addEventListener('click', () => selectTab(t));
    t.addEventListener('keydown', (e) => {
      const d = { ArrowRight: 1, ArrowLeft: -1 }[e.key];
      if (!d) return;
      e.preventDefault();
      const next = tabs[(i + d + tabs.length) % tabs.length];
      next.focus();
      selectTab(next);
    });
  });

  useChart(pageChart);
  renderRows();

  return {
    open(trigger, { size: pdpSize = '', onDismiss, chart: want = pageChart } = {}) {
      if (want !== chart) { useChart(want); renderRows(); }
      selectTab(tabs[0]);
      setSize(C.rows.some((r) => r[0] === pdpSize) ? pdpSize : '', { animate: false });
      drawer.open(trigger, { onDismiss });
    },
    close: () => drawer.close(),
    get isOpen() { return drawer.isOpen; },
  };
}
